import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { gql, useQuery, useMutation } from "@apollo/client";
import ModalWrapper from "components/Modal";
import { GET_AVAILABILITY } from "apollo/queries/events";
import { CREATE_BOOKING } from "apollo/mutations/booking";
import { listUserHorses } from "apollo/queries/horses";
import { useModalStore, Modal } from "stores/modal/useModalStore";

const LIST_USER_HORSES = gql`
  ${listUserHorses}
`;

dayjs.extend(utc);
dayjs.extend(tz);

const formatTime = (iso, zone) =>
  iso ? dayjs(iso).tz(zone || "America/Chicago").format("h:mm A") : "—";
const formatDay = (iso, zone) =>
  iso ? dayjs(iso).tz(zone || "America/Chicago").format("dddd, MMM D") : "—";

/**
 * In-site booking modal. Replaces the old react-calendly PopupModal.
 *
 * Props:
 *   isOpen     boolean
 *   onClose    () => void
 *   event      Event from useEventStore (must include id, dailyStartTime/end,
 *              startDate/endDate, eventName)
 *   user       optional logged-in user; we prefill email + name when present
 */
const BookingModal = ({ isOpen, onClose, event, user }) => {
  const minDate = useMemo(
    () => (event?.startDate ? event.startDate.slice(0, 10) : dayjs().format("YYYY-MM-DD")),
    [event]
  );
  const maxDate = useMemo(
    () => (event?.endDate ? event.endDate.slice(0, 10) : minDate),
    [event, minDate]
  );

  const [step, setStep] = useState("pick"); // pick → confirm → done
  const [date, setDate] = useState(minDate);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [form, setForm] = useState({
    customerName: user?.firstName ? `${user.firstName} ${user.lastName ?? ""}`.trim() : "",
    customerEmail: user?.email || "",
    customerPhone: "",
    notes: "",
  });
  const [errorText, setErrorText] = useState("");
  const openModal = useModalStore((s) => s.open);

  const { data: horseData, refetch: refetchHorses } = useQuery(LIST_USER_HORSES, {
    variables: { limit: 50 },
    skip: !isOpen || !user?.email,
    fetchPolicy: "cache-and-network",
  });
  const horses = (horseData?.listUserHorses?.items || []).filter((h) => !h.isDeleted);

  const { data: avail, loading: availLoading } = useQuery(GET_AVAILABILITY, {
    variables: { eventId: event?.id, date },
    skip: !isOpen || !event?.id || !date || step !== "pick",
    fetchPolicy: "network-only",
  });
  const slots = avail?.getAvailability || [];

  const [createBooking, { loading: submitting }] = useMutation(CREATE_BOOKING);

  const handleClose = () => {
    setStep("pick");
    setSelectedSlot("");
    setSelectedHorseIds([]);
    setErrorText("");
    onClose?.();
  };

  const toggleHorse = (id) =>
    setSelectedHorseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const goConfirm = () => {
    if (!selectedSlot) {
      setErrorText("Pick a time first.");
      return;
    }
    setErrorText("");
    setStep("confirm");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.customerEmail) {
      setErrorText("We need your name & email to lock this in.");
      return;
    }
    if (user?.email && horses.length > 0 && selectedHorseIds.length === 0) {
      setErrorText("Pick at least one horse for this fitting.");
      return;
    }
    try {
      const { data } = await createBooking({
        variables: {
          input: {
            eventId: event.id,
            customerName: form.customerName,
            customerEmail: form.customerEmail,
            customerPhone: form.customerPhone || null,
            horseIds: selectedHorseIds.length ? selectedHorseIds : null,
            startTime: selectedSlot,
            notes: form.notes || null,
          },
        },
      });
      const checkoutUrl = data?.createBooking?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      setStep("done");
    } catch (err) {
      setErrorText(err?.message || "Something went sideways. Mind trying again?");
    }
  };

  if (!event) return null;

  const field =
    "w-full mt-1 px-4 h-[44px] rounded-[8px] border border-[#c3c9d4] text-[14px] font-[Montserrat]";
  const label =
    "text-[12px] font-[Montserrat] text-[#2b364b] uppercase tracking-wide";

  return (
    <ModalWrapper
      modalIsOpen={isOpen}
      closeModal={handleClose}
      isCloseIcon
      className="!max-w-[560px]"
    >
      <div className="px-4 py-4 sm:px-6 sm:py-2">
        <h2 className="text-[#2b364b] font-[Montserrat] font-bold uppercase text-[32px] leading-tight">
          {step === "done" ? "You're All Set." : "Book Your Fitting"}
        </h2>
        <p className="text-[#5c80b6] font-[Montserrat] text-[14px] mt-1">{event.eventName}</p>
        {event.eventType ? (
          <p className="text-[#6b7280] font-[Montserrat] text-[13px] mt-0.5">{event.eventType}</p>
        ) : null}
        {event.sponsorshipTitle ? (
          <p className="text-[#6b7280] font-[Montserrat] text-[12px] mt-1 italic">{event.sponsorshipTitle}</p>
        ) : null}

        {step === "pick" && (
          <div className="mt-5 space-y-5">
            <div>
              <div className={label}>Pick A Day That Works</div>
              <input
                type="date"
                className={field}
                value={date}
                min={minDate}
                max={maxDate}
                onChange={(e) => {
                  setDate(e.target.value);
                  setSelectedSlot("");
                }}
              />
              <p className="text-[11px] text-[#6b7280] mt-1">
                {minDate}
                {maxDate && maxDate !== minDate ? ` – ${maxDate}` : ""}
              </p>
            </div>
            <div>
              <div className={label}>Choose Your Time</div>
              {availLoading ? (
                <div className="text-[12px] mt-2">Loading times…</div>
              ) : slots.length === 0 ? (
                <div className="text-[13px] mt-2 text-[#6b7280]">
                  No openings that day — try another.
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {slots.map((s) => (
                    <button
                      type="button"
                      key={s.startTime}
                      onClick={() => setSelectedSlot(s.startTime)}
                      className={`h-[42px] rounded-[8px] border text-[13px] font-[Montserrat] ${
                        selectedSlot === s.startTime
                          ? "bg-[#2b364b] text-white border-[#2b364b]"
                          : "bg-white text-[#2b364b] border-[#c3c9d4] hover:border-[#5c80b6]"
                      }`}
                    >
                      {formatTime(s.startTime, event?.timezone)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errorText && (
              <div className="text-[13px] text-[#f25022]">{errorText}</div>
            )}
            <div className="flex gap-3 justify-center pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="cancel_btns w-[140px] h-[48px] rounded-full border border-[#2b364b] text-[#2b364b]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={goConfirm}
                disabled={!selectedSlot}
                className="confirm_btn w-[160px] h-[48px] rounded-full bg-[#2b364b] text-white disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="text-[14px] font-[Montserrat] text-[#2b364b] bg-[#f1f3f5] rounded-[10px] p-3">
              <div>{formatDay(selectedSlot, event?.timezone)} · {formatTime(selectedSlot, event?.timezone)}</div>
              <div className="text-[12px] text-[#6b7280] mt-1">
                {event.fullPaymentAtBooking
                  ? "Full fitting price is due at checkout to confirm your slot (no separate deposit)."
                  : "$50 deposit due at checkout to hold your slot."}
              </div>
            </div>
            <div>
              <div className={label}>Your Horse(s)</div>
              {horses.length === 0 ? (
                <div className="mt-2 border border-dashed border-[#c3c9d4] rounded-[8px] p-3 text-center font-[Montserrat]">
                  <p className="text-[13px] text-[#6b7280] mb-2">
                    No horses on file yet — add one so the fitter knows what they&apos;re working with.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      openModal(Modal.AddHorse);
                      // Refetch when user comes back so the new horse appears.
                      setTimeout(() => refetchHorses(), 500);
                    }}
                    className="px-4 h-[36px] rounded-full bg-[#2b364b] text-white text-[13px]"
                  >
                    + Add a Horse
                  </button>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {horses.map((h) => {
                    const checked = selectedHorseIds.includes(h.id);
                    return (
                      <button
                        type="button"
                        key={h.id}
                        onClick={() => toggleHorse(h.id)}
                        className={`w-full flex items-center gap-3 px-3 h-[48px] rounded-[8px] border text-left font-[Montserrat] ${
                          checked
                            ? "border-[#2b364b] bg-[#f1f3f5]"
                            : "border-[#c3c9d4] bg-white hover:border-[#5c80b6]"
                        }`}
                      >
                        <span
                          className={`w-[18px] h-[18px] rounded border flex items-center justify-center text-[12px] ${
                            checked
                              ? "bg-[#2b364b] border-[#2b364b] text-white"
                              : "border-[#c3c9d4]"
                          }`}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {h.photo ? (
                          <img
                            src={h.photo}
                            alt=""
                            className="w-[32px] h-[32px] rounded-full object-cover"
                          />
                        ) : null}
                        <span className="flex-1 text-[14px] text-[#2b364b]">
                          {h.name}
                          {h.registeredName ? (
                            <span className="text-[12px] text-[#6b7280] ml-1">
                              · {h.registeredName}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      openModal(Modal.AddHorse);
                      setTimeout(() => refetchHorses(), 500);
                    }}
                    className="text-[12px] text-[#5c80b6] underline font-[Montserrat]"
                  >
                    + Add another horse
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className={label}>Your Name</div>
              <input
                className={field}
                value={form.customerName}
                onChange={set("customerName")}
              />
            </div>
            <div>
              <div className={label}>Email</div>
              <input
                type="email"
                className={field}
                value={form.customerEmail}
                onChange={set("customerEmail")}
              />
            </div>
            <div>
              <div className={label}>Phone (Optional)</div>
              <input className={field} value={form.customerPhone} onChange={set("customerPhone")} />
            </div>
            <div>
              <div className={label}>Anything We Should Know?</div>
              <textarea
                className={field + " !h-[70px] py-2"}
                value={form.notes}
                onChange={set("notes")}
              />
            </div>
            {errorText && (
              <div className="text-[13px] text-[#f25022]">{errorText}</div>
            )}
            <div className="flex gap-3 justify-center pt-2">
              <button
                type="button"
                onClick={() => setStep("pick")}
                className="cancel_btns w-[140px] h-[48px] rounded-full border border-[#2b364b] text-[#2b364b]"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="confirm_btn w-[160px] h-[48px] rounded-full bg-[#2b364b] text-white disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Confirm Booking"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="mt-6 text-center space-y-4 font-[Montserrat]">
            <p className="text-[15px] text-[#343434]">
              Thanks for riding with us. We&apos;ll see you {formatDay(selectedSlot, event?.timezone)} at{" "}
              {formatTime(selectedSlot, event?.timezone)}.
            </p>
            <p className="text-[13px] text-[#6b7280]">
              A confirmation will land in your inbox shortly.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="confirm_btn w-[160px] h-[48px] rounded-full bg-[#2b364b] text-white"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default BookingModal;
